/**
 * Amazon 商品ページ / 検索 URL の組み立て。
 *
 * Amazon.co.jp の `/dp/{ASIN}` は ISBN-10 (旧 ASIN 互換) のみ受け付ける。
 * ISBN-13 は `/dp/` では開けないため、以下の優先順で URL を決定する:
 *
 * 1. ISBN-10 が渡された / ISBN-13 (978 プレフィックス) から変換できる → `/dp/{isbn10}`
 * 2. ISBN-13 が 979 プレフィックス (ISBN-10 相当なし) → 検索 URL `?k={isbn13}`
 * 3. ISBN がない → タイトル検索 `?k={title}`
 * 4. どちらも無い → null (呼び出し側でボタン非表示)
 */

const BOOKS_SEARCH = 'https://www.amazon.co.jp/s'
const DP_BASE = 'https://www.amazon.co.jp/dp'

const AMAZON_HOSTS = ['amazon.co.jp', 'amazon.com', 'amzn.to', 'amzn.asia', 'a.co']

/** Amazon の商品 URL (短縮 URL を含む) かを判定する */
export const isAmazonUrl = (value: string): boolean => {
  let parsed: URL
  try {
    parsed = new URL(value.trim())
  } catch {
    return false
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return false
  const host = parsed.hostname.toLowerCase()
  return AMAZON_HOSTS.some((h) => host === h || host.endsWith(`.${h}`))
}

/**
 * ISBN-13 (978 で始まる 13 桁) を ISBN-10 に変換する。
 * 979 プレフィックスや不正な入力は null を返す。
 */
export const isbn13ToIsbn10 = (isbn13: string): string | null => {
  const digits = isbn13.replace(/[^0-9]/g, '')
  if (digits.length !== 13) return null
  if (!digits.startsWith('978')) return null

  const core = digits.slice(3, 12)
  let sum = 0
  for (let i = 0; i < 9; i += 1) {
    // core[i] は必ず 1 文字の数字 (上で digits を数字のみに正規化 & 長さ検証済み)
    const d = Number(core.charAt(i))
    sum += (10 - i) * d
  }
  const remainder = sum % 11
  const check = (11 - remainder) % 11
  const checkChar = check === 10 ? 'X' : String(check)
  return `${core}${checkChar}`
}

/**
 * ISBN 文字列を ISBN-10 に正規化する (10 桁ならそのまま、13 桁なら変換)。
 * 変換できない場合 (979 プレフィックス, フォーマット不正) は null。
 */
export const toIsbn10 = (isbn: string | null | undefined): string | null => {
  if (!isbn) return null
  const digits = isbn.replace(/[^0-9Xx]/g, '').toUpperCase()
  if (digits.length === 10) return digits
  if (digits.length === 13) return isbn13ToIsbn10(digits)
  return null
}

/**
 * 与えられた ISBN / タイトルから Amazon で開くべき URL を返す。
 * 何も情報がない場合は null。
 */
export const buildAmazonUrl = (isbn: string | null | undefined, title: string | null | undefined): string | null => {
  const isbn10 = toIsbn10(isbn)
  if (isbn10) return `${DP_BASE}/${isbn10}`

  // ISBN-13 (979) はそのまま検索キーワードにフォールバック
  if (isbn) {
    const digits = isbn.replace(/[^0-9Xx]/g, '')
    if (digits.length === 13) {
      return `${BOOKS_SEARCH}?k=${encodeURIComponent(digits)}&i=stripbooks`
    }
  }

  if (title && title.trim().length > 0) {
    return `${BOOKS_SEARCH}?k=${encodeURIComponent(title.trim())}&i=stripbooks`
  }

  return null
}
